import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { searchUsmaUsers, updatePersonalDetails } from "../../functions/user";
import { searchCities } from "../../functions/location";
import "./PersonalDetailsPage.css";

const PRIVACY_OPTIONS = [
  { value: "public", label: "Public", hint: "Anyone on UsmaApp" },
  { value: "friends", label: "Friends", hint: "Only your friends" },
  { value: "only_me", label: "Only me", hint: "Only you can see this" },
];

const DEFAULT_PRIVACY = {
  currentCity: "public",
  hometown: "public",
  dob: "friends",
  gender: "public",
  relationship: "public",
  family: "friends",
};

const RELATIONSHIP_OPTIONS = [
  "Single",
  "In a relationship",
  "Engaged",
  "Married",
  "It's complicated",
  "Divorced",
  "Widowed",
];

const FAMILY_RELATIONS = [
  "Mother",
  "Father",
  "Sister",
  "Brother",
  "Daughter",
  "Son",
  "Wife",
  "Husband",
  "Partner",
  "Grandmother",
  "Grandfather",
  "Aunt",
  "Uncle",
  "Cousin",
  "Other",
];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function getPrivacy(details, field) {
  const value = details?.detailsPrivacy?.[field];
  return PRIVACY_OPTIONS.some((option) => option.value === value)
    ? value
    : DEFAULT_PRIVACY[field] || "public";
}

function formatDob(bYear, bMonth, bDay) {
  if (!bYear || !bMonth || !bDay) return "";
  const monthName = MONTHS[bMonth - 1];
  if (!monthName) return "";
  return `${monthName} ${bDay}, ${bYear}`;
}

function formatGender(gender) {
  if (!gender) return "";
  return gender.charAt(0).toUpperCase() + gender.slice(1);
}

function memberName(member) {
  const person = member?.user;
  if (person && typeof person === "object") {
    return (
      person.name ||
      `${person.first_name || ""} ${person.last_name || ""}`.trim() ||
      person.username ||
      member.name ||
      ""
    );
  }
  return member?.name || "";
}

function memberKey(member) {
  const userId = member?.user?._id || (typeof member?.user === "string" ? member.user : "");
  if (userId) return `u:${userId}`;
  return `n:${(member?.name || "").trim().toLowerCase()}`;
}

function memberAvatar(member) {
  return member?.user?.picture || "/images/default_pic.png";
}

function PrivacyIcon({ level }) {
  if (level === "only_me") {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M17 9V7a5 5 0 0 0-10 0v2H5v12h14V9h-2zm-8 0V7a3 3 0 0 1 6 0v2H9z"
        />
      </svg>
    );
  }
  if (level === "friends") {
    return (
      <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
        <path
          fill="currentColor"
          d="M9 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm6 1a3.5 3.5 0 1 0-3.5-3.5A3.5 3.5 0 0 0 15 13zM9 14c-3.3 0-6 1.7-6 3.8V20h12v-2.2C15 15.7 12.3 14 9 14zm6 .5c-.4 0-.9 0-1.3.1 1.6.9 2.8 2.2 2.8 3.7V20h5v-1.5c0-1.9-2.3-3.9-6.5-4z"
        />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10 10 0 0 0 12 2zm7.4 9h-3.1a15.4 15.4 0 0 0-1.4-6 8 8 0 0 1 4.5 6zM12 4a13.6 13.6 0 0 1 1.8 7H10.2A13.6 13.6 0 0 1 12 4zM4.6 13h3.1a15.4 15.4 0 0 0 1.4 6 8 8 0 0 1-4.5-6zm3.1-2H4.6a8 8 0 0 1 4.5-6 15.4 15.4 0 0 0-1.4 6zM12 20a13.6 13.6 0 0 1-1.8-7h3.6A13.6 13.6 0 0 1 12 20zm2.9-1a15.4 15.4 0 0 0 1.4-6h3.1a8 8 0 0 1-4.5 6z"
      />
    </svg>
  );
}

function FieldIcon({ name }) {
  const icons = {
    city: (
      <path
        fill="currentColor"
        d="M12 2C8.1 2 5 5.1 5 9c0 5.2 7 13 7 13s7-7.8 7-13c0-3.9-3.1-7-7-7zm0 9.5A2.5 2.5 0 1 1 14.5 9 2.5 2.5 0 0 1 12 11.5z"
      />
    ),
    home: (
      <path fill="currentColor" d="M12 3 3 10h2v10h6v-6h2v6h6V10h2z" />
    ),
    cake: (
      <path
        fill="currentColor"
        d="M12 6a1.5 1.5 0 0 0 1.5-1.5c0-.8-1.5-2.5-1.5-2.5s-1.5 1.7-1.5 2.5A1.5 1.5 0 0 0 12 6zm7 7h-2V9H7v4H5a2 2 0 0 0-2 2v6h18v-6a2 2 0 0 0-2-2z"
      />
    ),
    gender: (
      <path
        fill="currentColor"
        d="M12 2a5 5 0 0 0-1 9.9V14H8v2h3v3h2v-3h3v-2h-3v-2.1A5 5 0 0 0 12 2z"
      />
    ),
    heart: (
      <path
        fill="currentColor"
        d="M12 21s-7.2-4.6-9.5-8.6C.6 9.2 2.2 5 6.1 5 8.1 5 9.6 6.1 12 8.4 14.4 6.1 15.9 5 17.9 5c3.9 0 5.5 4.2 3.6 7.4C19.2 16.4 12 21 12 21z"
      />
    ),
    family: (
      <path
        fill="currentColor"
        d="M8 10a3 3 0 1 0-3-3 3 3 0 0 0 3 3zm8 0a3 3 0 1 0-3-3 3 3 0 0 0 3 3zM8 12c-2.7 0-5 1.3-5 3v3h6.1A6 6 0 0 1 9 16a6 6 0 0 1 2.2-4.6A6.8 6.8 0 0 0 8 12zm8 0a6.8 6.8 0 0 0-3.2.9A6 6 0 0 1 15 16a6 6 0 0 1-.1 2H21v-3c0-1.7-2.3-3-5-3z"
      />
    ),
  };
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      {icons[name]}
    </svg>
  );
}

function PrivacyMenu({ field, value, openField, setOpenField, onChange, disabled }) {
  const menuRef = useRef(null);
  const isOpen = openField === field;
  const current = PRIVACY_OPTIONS.find((option) => option.value === value);

  useEffect(() => {
    if (!isOpen) return undefined;
    const handleClick = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setOpenField(null);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isOpen, setOpenField]);

  return (
    <div className="pd_privacy" ref={menuRef}>
      <button
        type="button"
        className={`pd_privacy_btn ${isOpen ? "active" : ""}`}
        onClick={() => setOpenField(isOpen ? null : field)}
        disabled={disabled}
        aria-label={`Privacy: ${current?.label || "Public"}`}
      >
        <PrivacyIcon level={value} />
        <span>{current?.label || "Public"}</span>
      </button>
      {isOpen && (
        <div className="pd_privacy_menu">
          {PRIVACY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`pd_privacy_option ${value === option.value ? "selected" : ""}`}
              onClick={() => {
                onChange(option.value);
                setOpenField(null);
              }}
            >
              <PrivacyIcon level={option.value} />
              <span>
                <strong>{option.label}</strong>
                <small>{option.hint}</small>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function PersonalDetailsPage({
  details,
  profile,
  setVisible,
  onSaved,
}) {
  const { user } = useSelector((state) => ({ ...state }));
  const [localDetails, setLocalDetails] = useState(details || {});
  const [gender, setGender] = useState(profile?.gender || "");
  const [bYear, setBYear] = useState(profile?.bYear || "");
  const [bMonth, setBMonth] = useState(profile?.bMonth || "");
  const [bDay, setBDay] = useState(profile?.bDay || "");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [privacyOpen, setPrivacyOpen] = useState(null);
  const [familyQuery, setFamilyQuery] = useState("");
  const [familyRelation, setFamilyRelation] = useState("Sister");
  const [selectedUser, setSelectedUser] = useState(null);
  const [userResults, setUserResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [manualName, setManualName] = useState("");
  const [familyDraft, setFamilyDraft] = useState([]);
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [citySearching, setCitySearching] = useState(false);

  useEffect(() => {
    setLocalDetails(details || {});
  }, [details]);

  useEffect(() => {
    setGender(profile?.gender || "");
    setBYear(profile?.bYear || "");
    setBMonth(profile?.bMonth || "");
    setBDay(profile?.bDay || "");
  }, [profile]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  useEffect(() => {
    const query = familyQuery.trim();
    if (query.length < 1 || selectedUser) {
      setUserResults([]);
      setSearchingUsers(false);
      return undefined;
    }

    let cancelled = false;
    setSearchingUsers(true);
    const timer = setTimeout(async () => {
      const results = await searchUsmaUsers(query, user.token);
      if (cancelled) return;
      const already = new Set(familyDraft.map(memberKey));
      const matches = results.filter((person) => !already.has(`u:${person._id}`));
      setUserResults(matches);
      if (matches.length === 0) {
        setManualName((prev) => prev || query);
      }
      setSearchingUsers(false);
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [familyQuery, selectedUser, user.token, familyDraft]);

  const applySaved = (data) => {
    setLocalDetails((prev) => ({
      ...prev,
      ...data,
      detailsPrivacy: data.detailsPrivacy || prev.detailsPrivacy,
      family: data.family || prev.family,
    }));
    if (data.gender) setGender(data.gender);
    if (data.bYear) setBYear(data.bYear);
    if (data.bMonth) setBMonth(data.bMonth);
    if (data.bDay) setBDay(data.bDay);
    onSaved?.(data);
  };

  const saveUpdate = async (payload) => {
    setSaving(true);
    setError("");
    const result = await updatePersonalDetails(payload, user.token);
    setSaving(false);
    if (result.status !== "ok") {
      setError(result.message);
      return false;
    }
    applySaved(result.data);
    return true;
  };

  const savePrivacy = async (field, value) => {
    await saveUpdate({
      infos: {
        detailsPrivacy: { [field]: value },
      },
    });
  };

  useEffect(() => {
    const query = typeof draft === "string" ? draft.trim() : "";
    if ((editing !== "currentCity" && editing !== "hometown") || query.length < 2) {
      setCitySuggestions([]);
      setCitySearching(false);
      return undefined;
    }

    let cancelled = false;
    setCitySearching(true);
    const timer = setTimeout(async () => {
      const result = await searchCities(query, user.token);
      if (cancelled) return;
      setCitySuggestions(Array.isArray(result.data) ? result.data : []);
      setCitySearching(false);
    }, 350);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [draft, editing, user.token]);

  const startEdit = (field) => {
    setError("");
    setPrivacyOpen(null);
    setEditing(field);
    setCitySuggestions([]);
    if (field === "currentCity") setDraft(localDetails.currentCity || "");
    if (field === "hometown") setDraft(localDetails.hometown || "");
    if (field === "relationship") setDraft(localDetails.relationship || "Single");
    if (field === "family") {
      setFamilyDraft(Array.isArray(localDetails.family) ? [...localDetails.family] : []);
      setSelectedUser(null);
      setFamilyQuery("");
      setManualName("");
      setUserResults([]);
    }
  };

  const saveField = async (field) => {
    if (field === "currentCity" || field === "hometown" || field === "relationship") {
      const ok = await saveUpdate({ infos: { [field]: String(draft || "").trim() } });
      if (ok) setEditing(null);
      return;
    }
    if (field === "family") {
      const ok = await saveUpdate({
        infos: {
          family: familyDraft.map((member) => ({
            user: member?.user?._id || member?.user || undefined,
            name: member.name || "",
            relationship: member.relationship,
          })),
        },
      });
      if (ok) setEditing(null);
    }
  };

  const removeField = async (field) => {
    let payload = null;
    if (field === "currentCity" || field === "hometown" || field === "relationship") {
      payload = { infos: { [field]: "" } };
    } else if (field === "family") {
      payload = { infos: { family: [] } };
    }
    if (!payload) return;
    const ok = await saveUpdate(payload);
    if (ok) setEditing(null);
  };

  const addFamilyMember = () => {
    if (selectedUser) {
      const already = familyDraft.some((member) => memberKey(member) === `u:${selectedUser._id}`);
      if (already) return;
      setFamilyDraft((prev) => [
        ...prev,
        { user: selectedUser, relationship: familyRelation },
      ]);
      setSelectedUser(null);
      setFamilyQuery("");
      setManualName("");
      return;
    }

    const name = (manualName || familyQuery).trim();
    if (!name) return;
    const already = familyDraft.some((member) => memberKey(member) === `n:${name.toLowerCase()}`);
    if (already) return;
    setFamilyDraft((prev) => [...prev, { name, relationship: familyRelation }]);
    setFamilyQuery("");
    setManualName("");
    setUserResults([]);
  };

  const rows = [
    {
      key: "currentCity",
      label: "City",
      icon: "city",
      value: localDetails.currentCity,
      empty: "Add your current city",
    },
    {
      key: "hometown",
      label: "Hometown",
      icon: "home",
      value: localDetails.hometown,
      empty: "Add your hometown",
    },
    {
      key: "dob",
      label: "Date of birth",
      icon: "cake",
      value: formatDob(bYear, bMonth, bDay),
      empty: "Added during registration",
      readOnly: true,
    },
    {
      key: "gender",
      label: "Gender",
      icon: "gender",
      value: formatGender(gender),
      empty: "Added during registration",
      readOnly: true,
    },
    {
      key: "relationship",
      label: "Relationship status",
      icon: "heart",
      value: localDetails.relationship,
      empty: "Add relationship status",
    },
    {
      key: "family",
      label: "Family",
      icon: "family",
      value: (localDetails.family || []).filter((member) => member?.user || member?.name),
      empty: "Add family members",
    },
  ];

  const locationSuggestions = citySuggestions;
  const isSearchingLocation = citySearching;
  const renderLocationSuggestions = () => {
    if (!isSearchingLocation && locationSuggestions.length === 0) return null;
    return (
      <div className="pd_friend_results">
        {isSearchingLocation && locationSuggestions.length === 0 && (
          <div className="pd_row_value empty">Searching locations...</div>
        )}
        {locationSuggestions.map((place) => (
          <button
            key={place.id}
            type="button"
            className="pd_friend_option"
            onClick={() => {
              setDraft((place.label || place.name || "").slice(0, 80));
              setCitySuggestions([]);
            }}
          >
            <span className="pd_location_pin" aria-hidden="true">📍</span>
            <span>
              <strong>{place.name}</strong>
              {place.label && place.label !== place.name ? (
                <small>{place.label}</small>
              ) : null}
            </span>
          </button>
        ))}
      </div>
    );
  };

  const page = (
    <div className="pd_overlay" onMouseDown={(event) => {
      if (event.target === event.currentTarget) setVisible(false);
    }}>
      <div className="pd_page" role="dialog" aria-labelledby="pd_title">
        <header className="pd_header">
          <button type="button" className="pd_back" onClick={() => setVisible(false)} aria-label="Close">
            <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
              <path fill="currentColor" d="M15.4 4.6 13.8 3 5 12l8.8 9 1.6-1.6L8.2 12z" />
            </svg>
          </button>
          <div>
            <h2 id="pd_title">Personal details</h2>
            <p>Choose what people see on your profile</p>
          </div>
        </header>

        <div className="pd_body">
          {error && <div className="pd_error">{error}</div>}

          {rows.map((row) => {
            const privacy = getPrivacy(localDetails, row.key);
            const isEditing = editing === row.key;
            return (
              <section key={row.key} className={`pd_row ${isEditing ? "editing" : ""}`}>
                <div className="pd_row_main">
                  <div className="pd_row_icon">
                    <FieldIcon name={row.icon} />
                  </div>
                  <div className="pd_row_copy">
                    <div className="pd_row_label">{row.label}</div>
                    {row.key !== "family" && !isEditing && (
                      <div className={`pd_row_value ${row.value ? "" : "empty"}`}>
                        {row.value || row.empty}
                      </div>
                    )}
                    {row.key === "family" && !isEditing && (
                      <div className="pd_family_preview">
                        {row.value.length ? (
                          row.value.map((member) => (
                            <div key={memberKey(member)} className="pd_family_chip">
                              <img
                                src={memberAvatar(member)}
                                alt=""
                              />
                              <span>
                                {memberName(member)}
                                {member.relationship ? ` · ${member.relationship}` : ""}
                              </span>
                            </div>
                          ))
                        ) : (
                          <div className="pd_row_value empty">{row.empty}</div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="pd_row_actions">
                    <PrivacyMenu
                      field={row.key}
                      value={privacy}
                      openField={privacyOpen}
                      setOpenField={setPrivacyOpen}
                      onChange={(value) => savePrivacy(row.key, value)}
                      disabled={saving}
                    />
                    {row.readOnly ? (
                      <span className="pd_locked" title="Collected during registration" aria-label="Locked">
                        🔒
                      </span>
                    ) : (
                      !isEditing && (
                        <button
                          type="button"
                          className="pd_edit_btn"
                          onClick={() => startEdit(row.key)}
                          aria-label="Edit"
                          title="Edit"
                        >
                          <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                            <path
                              fill="currentColor"
                              d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"
                            />
                          </svg>
                        </button>
                      )
                    )}
                  </div>
                </div>

                {isEditing && (row.key === "currentCity" || row.key === "hometown") && (
                  <div className="pd_editor">
                    <input
                      className="pd_input"
                      value={draft}
                      maxLength={80}
                      placeholder={row.key === "currentCity" ? "Search for a city" : "Search for a hometown"}
                      onChange={(event) => setDraft(event.target.value)}
                      autoFocus
                    />
                    {renderLocationSuggestions()}
                  </div>
                )}

                {isEditing && row.key === "relationship" && (
                  <div className="pd_editor">
                    <select
                      className="pd_input"
                      value={draft}
                      onChange={(event) => setDraft(event.target.value)}
                    >
                      {RELATIONSHIP_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                )}

                {isEditing && row.key === "family" && (
                  <div className="pd_editor pd_family_editor">
                    {familyDraft.map((member) => (
                      <div key={memberKey(member)} className="pd_family_edit_row">
                        <img src={memberAvatar(member)} alt="" />
                        <div>
                          <strong>{memberName(member)}</strong>
                          <select
                            className="pd_input"
                            value={member.relationship || "Other"}
                            onChange={(event) => {
                              const relationship = event.target.value;
                              setFamilyDraft((prev) =>
                                prev.map((item) =>
                                  memberKey(item) === memberKey(member)
                                    ? { ...item, relationship }
                                    : item
                                )
                              );
                            }}
                          >
                            {FAMILY_RELATIONS.map((relation) => (
                              <option key={relation} value={relation}>{relation}</option>
                            ))}
                          </select>
                        </div>
                        <button
                          type="button"
                          className="pd_remove"
                          onClick={() =>
                            setFamilyDraft((prev) =>
                              prev.filter((item) => memberKey(item) !== memberKey(member))
                            )
                          }
                        >
                          Remove
                        </button>
                      </div>
                    ))}

                    <div className="pd_add_family">
                      <input
                        className="pd_input"
                        value={familyQuery}
                        placeholder="Search or enter a name"
                        onChange={(event) => {
                          setFamilyQuery(event.target.value);
                          setSelectedUser(null);
                          setManualName("");
                        }}
                      />
                      <select
                        className="pd_input"
                        value={familyRelation}
                        onChange={(event) => setFamilyRelation(event.target.value)}
                      >
                        {FAMILY_RELATIONS.map((relation) => (
                          <option key={relation} value={relation}>{relation}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="gray_btn"
                        onClick={addFamilyMember}
                        disabled={
                          !selectedUser &&
                          (searchingUsers ||
                            userResults.length > 0 ||
                            !(manualName || familyQuery).trim())
                        }
                      >
                        Add
                      </button>
                    </div>
                    {familyQuery && !selectedUser && (
                      <div className="pd_friend_results">
                        {searchingUsers && (
                          <div className="pd_row_value empty">Searching UsmaApp users...</div>
                        )}
                        {!searchingUsers && userResults.length > 0 && userResults.map((person) => (
                          <button
                            key={person._id}
                            type="button"
                            className="pd_friend_option"
                            onClick={() => {
                              setSelectedUser(person);
                              setFamilyQuery(
                                `${person.first_name || ""} ${person.last_name || ""}`.trim()
                              );
                              setManualName("");
                            }}
                          >
                            <img src={person.picture || "/images/default_pic.png"} alt="" />
                            <span>
                              {person.first_name} {person.last_name}
                            </span>
                          </button>
                        ))}
                        {!searchingUsers && userResults.length === 0 && (
                          <div className="pd_add_manual">
                            <div className="pd_row_value empty">No matching UsmaApp users</div>
                            <div className="pd_add_manual_label">Add manually</div>
                            <input
                              className="pd_input"
                              value={manualName}
                              maxLength={80}
                              placeholder="Name"
                              onChange={(event) => setManualName(event.target.value)}
                            />
                            <select
                              className="pd_input"
                              value={familyRelation}
                              onChange={(event) => setFamilyRelation(event.target.value)}
                            >
                              {FAMILY_RELATIONS.map((relation) => (
                                <option key={relation} value={relation}>{relation}</option>
                              ))}
                            </select>
                          </div>
                        )}
                      </div>
                    )}
                    {selectedUser && (
                      <div className="pd_selected_friend">
                        Adding {selectedUser.first_name} {selectedUser.last_name} as {familyRelation}
                      </div>
                    )}
                  </div>
                )}

                {isEditing && (
                  <div className="pd_editor_actions">
                    {!!(Array.isArray(row.value) ? row.value.length : row.value) && (
                      <button
                        type="button"
                        className="pd_remove_field_btn"
                        onClick={() => removeField(row.key)}
                        disabled={saving}
                      >
                        {saving ? "Removing..." : "Remove"}
                      </button>
                    )}
                    <div className="pd_editor_actions_right">
                      <button type="button" className="gray_btn" onClick={() => setEditing(null)} disabled={saving}>
                        Cancel
                      </button>
                      <button type="button" className="blue_btn" onClick={() => saveField(row.key)} disabled={saving}>
                        {saving ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </div>
                )}
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );

  return createPortal(page, document.body);
}
